import { useMemo, useRef, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { CanceledError } from "axios";
import { useErrorBoundary } from "react-error-boundary";
import { deleteEmptyFieldsFromObject, setAllValuesToNull } from "@shared/lib/utils";
import { handleErrorWithBoundary } from "@shared/lib/errors";
import { Item, ItemSearch } from "@models/item";
import { buildItemSort, ItemSearchParams, searchItems } from "../../../api/items.service";
import { useEntityBrowse } from "../../../hooks/useEntityBrowse";
import { DEFAULT_SORT_KEY, parseItemsUrlParams, syncItemsUrlParams } from "./itemsUrlParams";

export function useItemsBrowse() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { showBoundary } = useErrorBoundary();

  const urlParams = useMemo(() => parseItemsUrlParams(searchParams), [searchParams]);

  const [data, setData] = useState<Item[]>([]);
  const [sortKey, setSortKey] = useState(urlParams.sortKey || DEFAULT_SORT_KEY);
  const [sortMethod, setSortMethod] = useState<"ASC" | "DESC">(urlParams.sortMethod);
  const [isLoading, setIsLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const itemSearchForm = useForm<ItemSearch>({
    defaultValues: urlParams.filters ?? {},
  });

  async function getData(searchData: ItemSearch): Promise<void> {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    setIsLoading(true);

    try {
      const sort = buildItemSort(sortKey, sortMethod);
      const payload: ItemSearchParams = {
        filter: deleteEmptyFieldsFromObject(searchData),
        sort: [sort],
      };
      const items = await searchItems(payload, abortControllerRef.current.signal);
      setData(items.data);
      setIsLoading(false);
    } catch (error: unknown) {
      if (error instanceof CanceledError) {
        return;
      }
      handleErrorWithBoundary(error, showBoundary);
      setIsLoading(false);
    }
  }

  const onSearch: SubmitHandler<ItemSearch> = (formData) => {
    syncItemsUrlParams(setSearchParams, formData, sortKey, sortMethod);
    getData(formData);
  };

  useEntityBrowse({
    form: itemSearchForm,
    sortKey,
    sortMethod,
    onSearch,
  });

  function handleSortKeyChanged(value: string) {
    setSortKey(value);
  }

  function handleSortMethodChanged(value: string) {
    setSortMethod(value === "ASC" ? "ASC" : "DESC");
  }

  function reset() {
    const values = setAllValuesToNull(itemSearchForm.getValues());

    for (const key in values) {
      if (Object.prototype.hasOwnProperty.call(values, key)) {
        itemSearchForm.setValue(key as keyof ItemSearch, values[key as keyof ItemSearch]);
      }
    }
  }

  return {
    itemSearchForm,
    data,
    isLoading,
    sortKey,
    sortMethod,
    handleSortKeyChanged,
    handleSortMethodChanged,
    reset,
  };
}
